import { getCurationContentCentres } from '../../helpers/curation-storefront'
import { FulfilmentCentre } from '../../redux-toolkits/fulfilment-centres/fulfilmentCentres.type'
import { useFormik } from 'formik'
import { ArrowLeft, ArrowRight, X } from 'lucide-react'
import { ChangeEvent, useEffect, useState } from 'react'
import { useModalControl } from '../../hooks/useModalControl'
import {
  useReorderListMenuItemsMutation,
  useUpdateListMenuMutation,
} from '../../redux-toolkits/list-menu/listMenu.slice'
import type {
  ListMenuItem,
  ListMenuItemEntry,
  ListMenuItemType,
} from '../../redux-toolkits/list-menu/listMenu.types'
import type { SingleSubCategory } from '../../redux-toolkits/products/products.type'
import type { Product } from '../../redux-toolkits/products/products.type'
import type { StrippedVendor } from '../../redux-toolkits/vendors/vendors.types'
import type { Article } from '../../redux-toolkits/lists/list.types'
import { NavMenuItemDetailSchema } from '../../validation/validate'
import { Button } from '../button'
import { ImageUploadButton } from '../button/imageUploadButton'
import { ImageBanner } from '../images/imageBanner'
import { TextInput } from '../inputs/textInput'
import ModalComponent from '../modal'
import { ImageUploadPreviewModal } from '../modal/app_modals/ImagePreviewModal'
import { SelectCategoriesModal } from '../modal/app_modals/selectCategoriesModal'
import { SelectCreatorModal } from '../modal/app_modals/selectCreatorModal'
import { SelectEssentialsModal } from '../modal/app_modals/selectEssentialModal'
import { SelectGoodListModal } from '../modal/app_modals/selectGoodListModal'
import { SelectProductModal } from '../modal/app_modals/selectProductModal'
import { SelectSubCategoriesModal } from '../modal/app_modals/selectSubCategoriesModal'
import { TextSubtext } from '../typography/textSubtext'

type Props = {
  menu: ListMenuItem
  fulfilmentCentres?: FulfilmentCentre[]
  onRefetch: () => void
  onChange?: (items: ListMenuItemEntry[]) => void
}

const ITEM_TYPES: { type: ListMenuItemType; label: string }[] = [
  { type: 'PRODUCTS', label: 'Products' },
  { type: 'GOOD_LISTS', label: 'Good Lists' },
  { type: 'CREATORS', label: 'Creators' },
  // { type: 'ESSENTIALS', label: 'Essentials' },
  { type: 'CATEGORIES', label: 'Categories' },
  { type: 'SUBCATEGORIES', label: 'Subcategories' },
]

const MAX_IMAGES = 2

export const NavMenuItems = ({
  menu,
  onRefetch,
  onChange,
  fulfilmentCentres,
}: Props) => {
  const contentCentres = getCurationContentCentres(fulfilmentCentres)
  const contentCentreId = contentCentres?.[0]?._id
  const [orderedItems, setOrderedItems] = useState(menu.items ?? [])
  const [showTypePicker, setShowTypePicker] = useState(false)
  const [pickerType, setPickerType] = useState<ListMenuItemType | null>(null)
  const [uploadedFile, setUploadedFile] = useState<File>()

  const { modal, modalOpen, setModalOpen, handleModalOpen, handleModalClose } =
    useModalControl()

  const [reorderListMenuItems, { isLoading: isReordering }] =
    useReorderListMenuItemsMutation()

  const [updateListMenu, { isLoading: isUpdating }] =
    useUpdateListMenuMutation()

  const isBusy = !onChange && (isReordering || isUpdating)

  useEffect(() => {
    setOrderedItems(menu.items ?? [])
  }, [menu.items])

  const closePicker = () => setPickerType(null)

  const closeDetails = () => {
    setUploadedFile(undefined)
    formik.resetForm()
  }

  const openDetails = async (
    type: ListMenuItemType,
    entity: { _id: string; name?: string; title?: string; images: string[] },
  ) => {
    await formik.setFieldValue('type', type)
    await formik.setFieldValue('item', entity._id)
    await formik.setFieldValue('title', entity.title ?? entity.name ?? '')
    await formik.setFieldValue('image', entity.images)
    await formik.validateForm()
    setPickerType(null)
  }

  const formik = useFormik({
    initialValues: {
      title: '',
      description: '',
      image: [],
      item: '',
      type: null,
    } as {
      title: string
      description: string
      image: string[]
      item: string
      type: ListMenuItemType | null
    },
    validationSchema: NavMenuItemDetailSchema,
    enableReinitialize: true,
    onSubmit: (values, { resetForm }) => {
      if (orderedItems.length >= 3) return
      const entry: ListMenuItemEntry = {
        item: values.item,
        type: values.type!,
        manualContent: {
          title: values.title,
          ...(values.description && { description: values.description }),
          ...(values.image.length > 0 && { image: values.image }),
        },
      }
      const next = [...orderedItems, entry]
      setOrderedItems(next)
      persistUpdate(next)
      resetForm()
    },
  })

  const normalizeItem = (entry: ListMenuItemEntry): ListMenuItemEntry => ({
    ...entry,
    item:
      typeof entry.item === 'object' && entry.item !== null
        ? (entry.item as unknown as { _id: string })._id
        : entry.item,
  })

  const persistReorder = async (items: ListMenuItemEntry[]) => {
    if (onChange) {
      onChange(items)
      return
    }
    await reorderListMenuItems({
      listMenuId: menu._id,
      itemIds: items.filter((i) => i._id).map((i) => i._id as string),
    })
    onRefetch()
  }

  const persistUpdate = async (items: ListMenuItemEntry[]) => {
    if (onChange) {
      onChange(items.map(normalizeItem))
      return
    }
    await updateListMenu({
      id: menu._id,
      body: { items: items.map(normalizeItem) },
    })
    onRefetch()
  }

  const handleMoveUp = (index: number) => {
    if (index === 0) return
    const next = [...orderedItems]
    ;[next[index - 1], next[index]] = [next[index], next[index - 1]]
    setOrderedItems(next)
    persistReorder(next)
  }

  const handleMoveDown = (index: number) => {
    if (index === orderedItems.length - 1) return
    const next = [...orderedItems]
    ;[next[index], next[index + 1]] = [next[index + 1], next[index]]
    setOrderedItems(next)
    persistReorder(next)
  }

  const handleRemove = (index: number) => {
    const next = orderedItems.filter((_, i) => i !== index)
    setOrderedItems(next)
    persistUpdate(next)
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return
    setUploadedFile(e.target.files[0])
    handleModalOpen('itemImageUpload')
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <TextSubtext text="Items" subtext="You can only add up to 3 items." />

        <Button
          type="button"
          label="Add Item"
          primary
          size="large"
          onClick={() => setShowTypePicker(true)}
          disabled={orderedItems.length >= 3}
        />
      </div>

      {orderedItems.length === 0 ? (
        <p className="text-sm text-neutral">No items added yet.</p>
      ) : (
        <div className="flex overflow-x-auto gap-5 -mx-6 px-6 pb-2">
          {orderedItems.map((item, index) => {
            const raw = item.manualContent.image as
              | string[]
              | string
              | undefined
            const images = Array.isArray(raw) ? raw : raw ? [raw] : []

            return (
              <div
                key={item._id ?? index}
                className="min-w-[282px] max-w-[282px] shrink-0"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex gap-0.5">
                    <button
                      type="button"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0 || isBusy}
                      className="p-1 rounded disabled:opacity-30 disabled:cursor-not-allowed text-sec-black"
                    >
                      <ArrowLeft size={14} />
                    </button>

                    <button
                      type="button"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === orderedItems.length - 1 || isBusy}
                      className="p-1 rounded disabled:opacity-30 disabled:cursor-not-allowed text-sec-black"
                    >
                      <ArrowRight size={14} />
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleRemove(index)}
                    disabled={isBusy}
                    className="p-1 rounded text-red-500 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <X size={14} />
                  </button>
                </div>

                <p className="text-xs font-medium text-sec-black tracking-wide uppercase mb-2 truncate">
                  {item.manualContent.title || '—'}
                </p>

                <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-bg-gray mb-2 group">
                  {images.length > 0 ? (
                    <>
                      <img
                        src={images[0]}
                        alt={item.manualContent.title}
                        className="w-full h-full object-cover"
                      />
                      {images[1] && (
                        <img
                          src={images[1]}
                          alt={item.manualContent.title}
                          className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        />
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-xs text-neutral">No image</span>
                    </div>
                  )}
                </div>

                <span className="inline-block text-[10px] font-medium uppercase tracking-wide bg-bg-gray text-neutral rounded px-2 py-0.5 mb-2">
                  {item.type.replace(/_/g, ' ')}
                </span>

                <p className="text-sm text-sec-black line-clamp-2">
                  {item.manualContent.description || '—'}
                </p>
              </div>
            )
          })}
        </div>
      )}

      {/* Type picker */}
      <ModalComponent
        open={showTypePicker}
        setOpen={setShowTypePicker}
        title="Select Item Type"
      >
        <div className="grid grid-cols-2 gap-3">
          {ITEM_TYPES.map(({ type, label }) => (
            <button
              key={type}
              type="button"
              onClick={() => {
                setShowTypePicker(false)
                setPickerType(type)
              }}
              className="border border-divider-gray rounded-lg p-4 text-sm font-medium text-left hover:border-primary hover:text-primary transition-colors"
            >
              {label}
            </button>
          ))}
        </div>
      </ModalComponent>

      {/* Item details form */}
      <ModalComponent
        open={formik.values.type !== null}
        setOpen={(open) => !open && closeDetails()}
        title="Item Details"
      >
        <div className="flex flex-col gap-4">
          <TextInput
            name="title"
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            errors={
              formik.touched.title && formik.errors.title
                ? formik.errors.title
                : ''
            }
            hasIcon={false}
            placeholder="Title"
          />

          <TextInput
            type="textarea"
            name="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            errors={
              formik.touched.description && formik.errors.description
                ? formik.errors.description
                : ''
            }
            hasIcon={false}
            placeholder="Description (optional)"
          />

          <div className="flex flex-col gap-2">
            <p className="text-sm text-neutral">
              Images (required, max {MAX_IMAGES})
            </p>
            <div className="flex flex-row gap-3 flex-wrap">
              {formik.values.image.map((src, idx) => (
                <ImageBanner
                  key={idx}
                  className="!h-[140px] !w-[140px]"
                  imageUrl={src}
                  onDelete={() => {
                    formik.setFieldValue(
                      'image',
                      formik.values.image.filter((_, i) => i !== idx),
                    )
                  }}
                />
              ))}
              {formik.values.image.length < MAX_IMAGES && (
                <ImageUploadButton
                  showText
                  handleChange={handleFileChange}
                  className="!relative !rounded-lg size-[140px] !m-0 !top-0 !right-0"
                  icon="pic-icon"
                />
              )}
            </div>
          </div>

          <Button
            type="button"
            primary
            label="Add Item"
            disabled={!formik.isValid || isBusy}
            loading={isBusy}
            onClick={() => formik.handleSubmit()}
          />
        </div>
      </ModalComponent>

      {/* Entity pickers */}
      <SelectProductModal
        query={{ fulfillmentCenterId: contentCentreId }}
        open={pickerType === 'PRODUCTS'}
        setOpen={(open) => !open && closePicker()}
        loading={isBusy}
        onSuccessCallback={([product]: Product[]) => {
          if (product)
            openDetails('PRODUCTS', {
              _id: product._id,
              name: product.name,
              images: product?.images?.slice(0, 2) ?? [],
            })
        }}
      />

      <SelectGoodListModal
        fulfilmentCentreId={contentCentreId}
        open={pickerType === 'GOOD_LISTS'}
        setOpen={(open) => !open && closePicker()}
        loading={isBusy}
        onSuccessCallback={([article]: Article[]) => {
          if (article)
            openDetails('GOOD_LISTS', {
              _id: article._id,
              title: article.title,
              images: article.bannerUrl ? [article.bannerUrl] : [],
            })
        }}
      />

      <SelectCreatorModal
        fulfillmentCenters={contentCentres}
        open={pickerType === 'CREATORS'}
        setOpen={(open) => !open && closePicker()}
        loading={isBusy}
        onSuccessCallback={([vendor]: StrippedVendor[]) => {
          if (vendor) {
            openDetails('CREATORS', {
              _id: vendor._id,
              name: vendor.name,
              images: vendor.banner ? [vendor.banner] : [],
            })
          }
        }}
      />

      <SelectEssentialsModal
        open={pickerType === 'ESSENTIALS'}
        setOpen={(open) => !open && closePicker()}
        loading={isBusy}
        onSuccessCallback={([essential]) => {
          if (essential)
            openDetails('ESSENTIALS', {
              _id: essential._id,
              title: essential.title,
              images: essential.banner ? [essential.banner] : [],
            })
        }}
      />

      <SelectCategoriesModal
        open={pickerType === 'CATEGORIES'}
        setOpen={(open) => !open && closePicker()}
        onSuccessCallback={([category]) => {
          if (category)
            openDetails('CATEGORIES', {
              _id: category._id,
              name: category.name,
              images: [],
            })
        }}
      />

      <SelectSubCategoriesModal
        open={pickerType === 'SUBCATEGORIES'}
        setOpen={(open) => !open && closePicker()}
        onSuccessCallback={([sub]: SingleSubCategory[]) => {
          if (sub)
            openDetails('SUBCATEGORIES', {
              _id: sub._id,
              name: sub.name,
              images: [],
            })
        }}
      />

      {uploadedFile && (
        <ImageUploadPreviewModal
          image={uploadedFile}
          open={modal === 'itemImageUpload' && modalOpen}
          setOpen={setModalOpen}
          onCloseCallback={() => {
            setUploadedFile(undefined)
            handleModalClose()
          }}
          uploadMode="item-image"
          onSuccessCallback={(url) => {
            formik.setFieldValue('image', [...formik.values.image, url])
          }}
        />
      )}
    </div>
  )
}
